import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Vehicle, VehicleCreate, VehicleUpdate } from '../models/Vehicle';
import { VehicleAttachment } from '../models/VehicleAttachment';
import { VehicleAttachmentsService, VehicleAttachmentUploadMeta } from './vehicle-attachments.service';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface VehiclePaginatedResponse {
  items: Vehicle[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/** Item retornado por GET /export/tables */
export interface ExportTableInfo {
  schema: string;
  tableName: string;
  fullName: string;
}

export interface VehicleExportColumnsResponse {
  tableName: string;
  columns: string[];
}

@Injectable({ providedIn: 'root' })
export class VehiclesService {
  private baseUrl = environment.microservices.vehicles;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private vehicleAttachmentsService: VehicleAttachmentsService
  ) {}

  private buildPaginationParams(
    page?: number, 
    pageSize?: number, 
    filters?: { status?: string; brand?: string; ownershipType?: string; search?: string },
    sort?: { sortBy: string; sortOrder: 'asc' | 'desc' }
  ): HttpParams {
    const p = Math.max(1, page ?? 1);
    const s = Math.min(100, Math.max(1, pageSize ?? 10));
    let params = new HttpParams().set('page', String(p)).set('pageSize', String(s));
    
    // Adicionar filtros opcionais
    if (filters) {
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.brand) {
        params = params.set('brand', filters.brand);
      }
      if (filters.ownershipType) {
        params = params.set('ownershipType', filters.ownershipType);
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
    }

    // Ordenação: API espera sortBy=camelCase (ex.: plateNumber) e sortOrder=asc|desc
    if (sort?.sortBy) {
      params = params.set('sortBy', sort.sortBy).set('sortOrder', sort.sortOrder || 'asc');
    }
    
    return params;
  }

  private mapPaginatedResponse(response: any): VehiclePaginatedResponse {
    // Se a resposta já é um array (formato antigo), converter para formato paginado
    if (Array.isArray(response)) {
      return {
        items: this.mapListFromDto(response),
        page: 1,
        pageSize: response.length,
        totalItems: response.length,
        totalPages: 1
      };
    }

    // Formato novo: objeto com items, page, pageSize, totalItems, totalPages
    const rawItems = response?.items ?? [];
    return {
      items: this.mapListFromDto(rawItems),
      page: response?.page ?? 1,
      pageSize: response?.pageSize ?? rawItems.length,
      totalItems: response?.totalItems ?? rawItems.length,
      totalPages: response?.totalPages ?? 1
    };
  }

  /**
   * Algumas rotas retornam envelope (ex.: { data }, { item }, { vehicle }).
   * Normaliza para o objeto do veículo em si.
   */
  private extractItem(response: any): any {
    return (
      response?.data?.data ??
      response?.data?.item ??
      response?.data?.vehicle ??
      response?.data ??
      response?.item ??
      response?.vehicle ??
      response?.result ??
      response
    );
  }

  // CRUD
  getAll(page?: number, pageSize?: number, filters?: { status?: string; brand?: string; ownershipType?: string; search?: string }): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles`, { params: this.buildPaginationParams(page, pageSize, filters) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos'); return throwError(() => err); })
    );
  }

  getAllPaginated(
    page?: number, 
    pageSize?: number, 
    filters?: { status?: string; brand?: string; ownershipType?: string; search?: string },
    sort?: { sortBy: string; sortOrder: 'asc' | 'desc' }
  ): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles`, { params: this.buildPaginationParams(page, pageSize, filters, sort) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos'); return throwError(() => err); })
    );
  }

  getById(id: number): Observable<Vehicle> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/${id}`).pipe(
      map(resp => this.mapFromDto(this.extractItem(resp))),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículo'); return throwError(() => err); })
    );
  }

  create(payload: VehicleCreate): Observable<Vehicle> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(`${this.baseUrl}/vehicles`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao criar veículo'); return throwError(() => err); })
    );
  }

  update(id: number, payload: VehicleUpdate): Observable<Vehicle> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/vehicles/${id}`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar veículo'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/vehicles/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao excluir veículo'); return throwError(() => err); })
    );
  }

  // Consultas adicionais
  getByPlate(plate: string): Observable<Vehicle | null> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/plate/${encodeURIComponent(plate)}`).pipe(
      map(resp => this.mapFromDto(this.extractItem(resp))),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículo por placa'); return throwError(() => err); })
    );
  }

  getByChassis(chassis: string): Observable<Vehicle | null> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/chassis/${encodeURIComponent(chassis)}`).pipe(
      map(resp => this.mapFromDto(this.extractItem(resp))),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículo por chassi'); return throwError(() => err); })
    );
  }

  getByStatus(status: string, page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/status/${encodeURIComponent(status)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por status'); return throwError(() => err); })
    );
  }

  getByStatusPaginated(status: string, page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/status/${encodeURIComponent(status)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por status'); return throwError(() => err); })
    );
  }

  getActive(page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/active`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos ativos'); return throwError(() => err); })
    );
  }

  getActivePaginated(page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/active`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos ativos'); return throwError(() => err); })
    );
  }

  getByOwnershipType(type: string, page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/ownership/${encodeURIComponent(type)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por tipo de propriedade'); return throwError(() => err); })
    );
  }

  getByOwnershipTypePaginated(type: string, page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/ownership/${encodeURIComponent(type)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por tipo de propriedade'); return throwError(() => err); })
    );
  }

  getByBrand(brand: string, page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/brand/${encodeURIComponent(brand)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por marca'); return throwError(() => err); })
    );
  }

  getByBrandPaginated(brand: string, page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/brand/${encodeURIComponent(brand)}`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos por marca'); return throwError(() => err); })
    );
  }

  getNeedingMaintenance(page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/maintenance-needed`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos que necessitam manutenção'); return throwError(() => err); })
    );
  }

  getNeedingMaintenancePaginated(page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/maintenance-needed`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar veículos que necessitam manutenção'); return throwError(() => err); })
    );
  }

  getFleet(page?: number, pageSize?: number): Observable<Vehicle[]> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/fleet`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp).items),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar frota de veículos'); return throwError(() => err); })
    );
  }

  getFleetPaginated(page?: number, pageSize?: number): Observable<VehiclePaginatedResponse> {
    return this.http.get<any>(`${this.baseUrl}/vehicles/fleet`, { params: this.buildPaginationParams(page, pageSize) }).pipe(
      map(resp => this.mapPaginatedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar frota de veículos'); return throwError(() => err); })
    );
  }

  // Ações de negócio
  assignToStaff(id: number, staffId: number, driverName: string): Observable<Vehicle> {
    return this.http.post<any>(`${this.baseUrl}/vehicles/${id}/assign`, { staffId, driverName }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atribuir veículo ao funcionário'); return throwError(() => err); })
    );
  }

  unassignFromStaff(id: number): Observable<Vehicle> {
    return this.http.post<any>(`${this.baseUrl}/vehicles/${id}/unassign`, {}).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao desatribuir veículo do funcionário'); return throwError(() => err); })
    );
  }

  updateMaintenanceStatus(id: number, requiresMaintenance: boolean, nextMaintenanceDate?: string): Observable<Vehicle> {
    return this.http.post<any>(`${this.baseUrl}/vehicles/${id}/maintenance-status`, { requiresMaintenance, nextMaintenanceDate }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar status de manutenção'); return throwError(() => err); })
    );
  }

  updateMileage(id: number, currentMileage: number): Observable<Vehicle> {
    return this.http.post<any>(`${this.baseUrl}/vehicles/${id}/mileage`, { currentMileage }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar quilometragem'); return throwError(() => err); })
    );
  }

  updateLocation(id: number, location: string): Observable<Vehicle> {
    return this.http.post<any>(`${this.baseUrl}/vehicles/${id}/location`, { location }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar localização'); return throwError(() => err); })
    );
  }

  validate(vehicle: VehicleCreate): Observable<{ isValid: boolean; errors: string[] }> {
    const dto = this.mapToDto(vehicle);
    return this.http.post<{ isValid: boolean; errors: string[] }>(`${this.baseUrl}/vehicles/validate`, dto).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao validar veículo'); return throwError(() => err); })
    );
  }

  // ===== Exportação Excel (tabela Vehicles) =====

  /** Lista colunas disponíveis para exportação da tabela Vehicles. */
  getVehicleExportColumns(): Observable<VehicleExportColumnsResponse> {
    return this.http.get<VehicleExportColumnsResponse>(`${this.baseUrl}/export/tables/Vehicles/columns`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao listar colunas para exportação');
        return throwError(() => err);
      })
    );
  }

  /**
   * Exporta a tabela Vehicles para Excel com colunas selecionadas.
   * Body: { tableNames: ["Vehicles"], columnsByTable: { Vehicles: string[] } }
   */
  exportVehiclesToExcel(columns: string[]): Observable<Blob> {
    const url = `${this.baseUrl}/export/excel`;
    const body = {
      tableNames: ['Vehicles'],
      columnsByTable: { Vehicles: columns }
    };
    return this.http.post(url, body, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map(response => {
        const blob = response.body;
        if (!blob) throw new Error('Resposta vazia do servidor');
        if (!blob.type || !blob.type.includes('spreadsheet')) {
          return new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
        return blob;
      }),
      catchError(err => {
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            try {
              const errorText = JSON.parse(reader.result as string);
              this.errorService.showErrorMessage(errorText.message || 'Erro ao exportar Excel');
            } catch {
              this.errorService.showErrorMessage('Erro ao exportar Excel');
            }
          };
          reader.readAsText(err.error);
        } else {
          this.errorService.handleHttpError(err, 'Erro ao exportar Excel');
        }
        return throwError(() => err);
      })
    );
  }

  // ===== Anexos (seguindo estilo Suppliers) =====
  listAttachments(vehicleId: number, opts?: { type?: string; fileType?: string; publicOnly?: boolean }): Observable<VehicleAttachment[]> {
    return this.vehicleAttachmentsService.list(vehicleId, opts);
  }

  uploadAttachment(vehicleId: number, file: File, meta?: VehicleAttachmentUploadMeta): Observable<VehicleAttachment> {
    return this.vehicleAttachmentsService.uploadAndRegister(vehicleId, file, meta);
  }

  getAttachment(attachmentId: number): Observable<VehicleAttachment> {
    return this.vehicleAttachmentsService.getById(attachmentId);
  }

  removeAttachment(attachmentId: number): Observable<void> {
    return this.vehicleAttachmentsService.remove(attachmentId);
  }

  replaceAttachment(attachmentId: number, file: File): Observable<VehicleAttachment> {
    return this.vehicleAttachmentsService.replaceAndRegister(attachmentId, file);
  }

  downloadAttachment(att: VehicleAttachment): Observable<Blob> {
    return this.vehicleAttachmentsService.downloadAttachment(att);
  }

  // Mapeamentos DTO <-> Front
  private mapFromDto(dto: any): Vehicle {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      plateNumber: pick(dto, ['PlateNumber', 'plateNumber', 'Plate', 'plate', 'Placa', 'placa']) ?? '',
      chassisNumber: pick(dto, ['ChassisNumber', 'chassisNumber', 'Chassis', 'chassis', 'Chassi', 'chassi']) ?? '',
      renavamNumber: pick(dto, ['RenavamNumber', 'renavamNumber']) ?? '',
      brand: pick(dto, ['Brand', 'brand', 'Marca', 'marca', 'Make', 'make']) ?? '',
      model: pick(dto, ['Model', 'model', 'Modelo', 'modelo']) ?? '',
      modelYear: pick(dto, ['ModelYear', 'modelYear', 'Year', 'year', 'AnoModelo', 'anoModelo']) ?? 0,
      manufactureYear: pick(dto, ['ManufactureYear', 'manufactureYear', 'AnoFabricacao', 'anoFabricacao']) ?? 0,
      color: pick(dto, ['Color', 'color', 'Cor', 'cor']),
      fuelType: pick(dto, ['FuelType', 'fuelType']),
      renavam: pick(dto, ['RenavamNumber', 'renavamNumber', 'Renavam', 'renavam']),
      trackerId: pick(dto, ['TrackerId', 'trackerId']),
      hasBox: pick(dto, ['HasBox', 'hasBox']),
      hasPhoneSupport: pick(dto, ['HasPhoneSupport', 'hasPhoneSupport']),
      ownershipType: pick(dto, ['OwnershipType', 'ownershipType']) ?? '',
      status: pick(dto, ['Status', 'status']) ?? 'Ativo',
      purchaseDate: toIso(pick(dto, ['PurchaseDate', 'purchaseDate'])),
      purchasePrice: pick(dto, ['PurchasePrice', 'purchasePrice']),
      currentMileage: pick(dto, ['CurrentMileage', 'currentMileage']),
      currentLocation: pick(dto, ['CurrentLocation', 'currentLocation']),
      keyCode: pick(dto, ['KeyCode', 'keyCode']),
      lastMaintenanceDate: toIso(pick(dto, ['LastMaintenanceDate', 'lastMaintenanceDate'])),
      lastMaintenanceMileage: pick(dto, ['LastMaintenanceMileage', 'lastMaintenanceMileage']),
      notes: pick(dto, ['Notes', 'notes']),
      requiresMaintenance: pick(dto, ['RequiresMaintenance', 'requiresMaintenance']),
      nextMaintenanceDate: toIso(pick(dto, ['NextMaintenanceDate', 'nextMaintenanceDate'])),
      assignedStaffId: pick(dto, ['AssignedStaffId', 'assignedStaffId']),
      assignedDriverName: pick(dto, ['AssignedDriverName', 'assignedDriverName']),
      assignmentDate: toIso(pick(dto, ['AssignmentDate', 'assignmentDate'])),
      assignmentEndDate: toIso(pick(dto, ['AssignmentEndDate', 'assignmentEndDate'])),
      b2BContractId: pick(dto, ['B2BContractId', 'b2BContractId']) ?? null,
      b2CContractId: pick(dto, ['B2CContractId', 'b2CContractId']) ?? null,
      attachments: this.mapAttachmentsArrayFromDto(dto?.Attachments ?? dto?.attachments)
    };
  }

  /** Normaliza anexos vindos no próprio GET do veículo (URLs do blob em filePath). Aceita array ou resposta paginada `{ items }`. */
  private mapAttachmentsArrayFromDto(raw: any): VehicleAttachment[] {
    if (raw == null) return [];
    const arr = Array.isArray(raw) ? raw : raw?.items;
    if (!Array.isArray(arr)) return [];
    return arr.map((a) => this.mapAttachmentFromDto(a));
  }

  private mapAttachmentFromDto(a: any): VehicleAttachment {
    const pick = (o: any, keys: string[]) => keys.map((k) => o?.[k]).find((v) => v !== undefined);
    return {
      id: pick(a, ['Id', 'id']) ?? 0,
      vehicleId: pick(a, ['VehicleId', 'vehicleId']) ?? 0,
      fileName: pick(a, ['FileName', 'fileName']) ?? '',
      filePath: pick(a, ['FilePath', 'filePath']) ?? '',
      fileType: pick(a, ['FileType', 'fileType']) ?? '',
      fileSize: pick(a, ['FileSize', 'fileSize']),
      attachmentType: pick(a, ['AttachmentType', 'attachmentType']),
      description: pick(a, ['Description', 'description']),
      isPublic: pick(a, ['IsPublic', 'isPublic']),
      uploadedBy: pick(a, ['UploadedBy', 'uploadedBy']),
      tags: pick(a, ['Tags', 'tags']),
      createdAt: pick(a, ['CreatedAt', 'createdAt', 'UploadDate', 'uploadDate']),
      updatedAt: pick(a, ['UpdatedAt', 'updatedAt'])
    };
  }

  private mapListFromDto(list: any[]): Vehicle[] {
    if (!Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  private mapToDto(payload: Partial<VehicleCreate | VehicleUpdate>): any {
    // Aceita renavamNumber diretamente ou renavam (compatibilidade)
    const renavamNumberRaw = (payload as any).renavamNumber ?? payload.renavam;
    const renavamNumber = renavamNumberRaw !== undefined && renavamNumberRaw !== null
      ? String(renavamNumberRaw)
      : '';

    const dto: any = {
      plateNumber: payload.plateNumber,
      chassisNumber: payload.chassisNumber,
      renavamNumber, // sempre envia como string
      brand: payload.brand,
      model: payload.model,
      modelYear: payload.modelYear,
      manufactureYear: payload.manufactureYear,
      color: payload.color,
      fuelType: payload.fuelType,
      trackerId: payload.trackerId,
      hasBox: payload.hasBox,
      hasPhoneSupport: payload.hasPhoneSupport,
      ownershipType: payload.ownershipType,
      status: payload.status,
      purchaseDate: payload.purchaseDate,
      purchasePrice: payload.purchasePrice,
      currentMileage: payload.currentMileage,
      currentLocation: payload.currentLocation,
      keyCode: payload.keyCode,
      notes: payload.notes,
      requiresMaintenance: payload.requiresMaintenance,
      nextMaintenanceDate: payload.nextMaintenanceDate,
      assignedStaffId: payload.assignedStaffId,
      assignedDriverName: payload.assignedDriverName
    };

    return dto;
  }
}
